import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import { landingFor } from "../lib/roles.js";

/**
 * Family signup — the account-creation half of family onboarding (§7.2).
 *
 * Creates the account and the owner, and nothing else. Creating the person the
 * profile is about is the NEXT step, on the family dashboard: it carries their
 * name, pronouns, and where they live, and it deserves its own moment rather
 * than being buried in a form about email addresses.
 *
 * Copy discipline is the same as the entry fork — this person may never have
 * heard the word "client," and shouldn't have to.
 */
export default function FamilySignup() {
  const { user, signup, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]   = useState({ full_name: "", email: "", password: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);

  if (!loading && user) return <Navigate to={landingFor(user)} replace />;

  function set(key, val) { setForm(prev => ({ ...prev, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!form.full_name.trim())      { setError("Please enter your name."); return; }
    if (!form.email.includes("@"))   { setError("Please enter a valid email address."); return; }
    if (form.password.length < 8)    { setError("Please choose a password of at least 8 characters."); return; }

    setBusy(true);
    try {
      const data = await signup({
        full_name: form.full_name.trim(),
        email:     form.email.trim(),
        password:  form.password,
      });
      navigate(data.redirect, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <Link to="/" style={s.back}>← Back</Link>

        <div style={s.card}>
          <h1 style={s.heading}>Let's start with you</h1>
          <p style={s.sub}>
            You'll add the person you care for in a moment. First, an account so
            your work is saved and you can share it when you're ready.
          </p>

          <form onSubmit={handleSubmit} style={s.form}>
            <label style={s.label} htmlFor="full_name">Your name</label>
            <input
              id="full_name" type="text" style={s.input} autoFocus
              value={form.full_name} onChange={e => set("full_name", e.target.value)}
              autoComplete="name" disabled={busy}
            />

            <label style={s.label} htmlFor="email">Email</label>
            <input
              id="email" type="email" style={s.input}
              value={form.email} onChange={e => set("email", e.target.value)}
              autoComplete="email" disabled={busy}
            />

            <label style={s.label} htmlFor="password">Password</label>
            <input
              id="password" type="password" style={s.input}
              value={form.password} onChange={e => set("password", e.target.value)}
              autoComplete="new-password" disabled={busy}
            />
            <p style={s.hint}>At least 8 characters.</p>

            {error && <p style={s.error}>{error}</p>}

            <button type="submit" style={{ ...s.btn, ...(busy ? s.btnBusy : {}) }} disabled={busy}>
              {busy ? "Creating your account…" : "Create account"}
            </button>
          </form>

          <p style={s.footer}>
            Already have an account? <Link to="/login" style={s.link}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:     { minHeight: "100dvh", background: "var(--color-bg-subtle)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  inner:    { width: "100%", maxWidth: 420 },
  back:     { display: "inline-block", marginBottom: 16, fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", textDecoration: "none" },
  card:     { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 36px" },
  heading:  { fontSize: "var(--text-xl)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", marginBottom: 6 },
  sub:      { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", marginBottom: 24 },
  form:     { display: "flex", flexDirection: "column", gap: 0 },
  label:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginBottom: 6, display: "block" },
  input:    { width: "100%", padding: "10px 12px", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: "var(--text-base)", background: "var(--color-bg-subtle)", color: "var(--color-text-primary)", fontFamily: "inherit", marginBottom: 16, outline: "none", boxSizing: "border-box" },
  hint:     { fontSize: 11, color: "var(--color-text-tertiary)", marginTop: -12, marginBottom: 16 },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "8px 12px", marginBottom: 12 },
  btn:      { width: "100%", padding: "11px 16px", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer", marginTop: 4 },
  btnBusy:  { opacity: 0.6, cursor: "not-allowed" },
  footer:   { textAlign: "center", marginTop: 20, fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
  link:     { color: "var(--color-accent)" },
};
